import React, { useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { toast } from "react-toastify";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function PaidExpenseReport() {
  const F11_BRANCH_ID = 15;
  const G13_BRANCH_ID = 1;
  const CASH_FROM_BANK_CATEGORY = "cash_from_bank";
  const CASH_IN_HAND_CATEGORY = "cash_in-hand";

  const [filters, setFilters] = useState({
    date: new Date().toLocaleDateString("en-CA"),
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    if (!filters.date) {
      toast.error("Please select a date", { autoClose: 3000 });
      return;
    }

    setLoading(true);
    try {
      const response = await UserServices.getPaidExpenseReport({
        date: filters.date,
        cash_from_bank_category: CASH_FROM_BANK_CATEGORY,
        cash_in_hand_category: CASH_IN_HAND_CATEGORY,
      });
      setReport(response.data.data || null);
    } catch (err) {
      console.error(err);
      setReport(null);
      toast.error("No record found", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const formatAmount = (value) => {
    const num = Number(value || 0);
    return Number.isNaN(num) ? "0" : num.toLocaleString("en-US");
  };

  const branchData = report?.branches || [];
  const leftBranch = branchData.find((b) => b.branch_id === F11_BRANCH_ID) || {
    cash_expenses: [],
    bank_expenses: [],
  };
  const rightBranch = branchData.find((b) => b.branch_id === G13_BRANCH_ID) || {
    cash_expenses: [],
    bank_expenses: [],
  };

  const maxCashRows = Math.max(
    leftBranch.cash_expenses.length,
    rightBranch.cash_expenses.length
  );
  const maxBankRows = Math.max(
    leftBranch.bank_expenses.length,
    rightBranch.bank_expenses.length
  );

  const exportLink = `${process.env.REACT_APP_API_Link}/expense/paid-export?date=${filters.date}&cash_from_bank_category=${CASH_FROM_BANK_CATEGORY}&cash_in_hand_category=${CASH_IN_HAND_CATEGORY}`;

  const generatePdf = () => {
    if (!report) {
      toast.error("No data to export", { autoClose: 3000 });
      return;
    }

    const doc = new jsPDF("p", "mm", "letter");
    const titleText = `PAID Expenses ${moment(filters.date).format("DD-MMM-YYYY")}`;

    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(0, 160, 80);
    doc.rect(10, 10, pageWidth - 20, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text(titleText, pageWidth / 2, 15.5, { align: "center" });

    const rows = [];
    const branchStyle = { fillColor: [0, 153, 51], textColor: 255, halign: "center", fontStyle: "bold" };
    const headerStyle = { fillColor: [217, 217, 217], fontStyle: "bold", halign: "center" };

    rows.push([
      { content: "F-11 Exp Paid Detail", colSpan: 2, styles: branchStyle },
      { content: "G-13 Exp Paid Detail", colSpan: 2, styles: branchStyle },
    ]);
    rows.push([
      "Cash from Bank",
      formatAmount(leftBranch.cash_from_bank),
      "Cash from Bank",
      formatAmount(rightBranch.cash_from_bank),
    ]);
    rows.push([
      "Cash in hand",
      formatAmount(leftBranch.cash_in_hand),
      "Cash in hand",
      formatAmount(rightBranch.cash_in_hand),
    ]);
    rows.push([
      { content: "Total Cash", styles: headerStyle },
      { content: formatAmount(leftBranch.total_cash), styles: headerStyle },
      { content: "Total Cash", styles: headerStyle },
      { content: formatAmount(rightBranch.total_cash), styles: headerStyle },
    ]);

    for (let i = 0; i < maxCashRows; i += 1) {
      rows.push([
        leftBranch.cash_expenses[i]?.description || "",
        formatAmount(leftBranch.cash_expenses[i]?.amount),
        rightBranch.cash_expenses[i]?.description || "",
        formatAmount(rightBranch.cash_expenses[i]?.amount),
      ]);
    }

    rows.push([
      { content: "Total Expense", styles: headerStyle },
      { content: formatAmount(leftBranch.total_expense), styles: headerStyle },
      { content: "Total Expense", styles: headerStyle },
      { content: formatAmount(rightBranch.total_expense), styles: headerStyle },
    ]);
    rows.push([
      "Balance Cash in Safe",
      formatAmount(leftBranch.balance_cash_in_safe),
      "Balance Cash in Safe",
      formatAmount(rightBranch.balance_cash_in_safe),
    ]);

    rows.push([
      { content: "Bank Payment Detail", colSpan: 2, styles: branchStyle },
      { content: "Bank Payment Detail", colSpan: 2, styles: branchStyle },
    ]);

    for (let i = 0; i < maxBankRows; i += 1) {
      rows.push([
        leftBranch.bank_expenses[i]?.description || "",
        formatAmount(leftBranch.bank_expenses[i]?.amount),
        rightBranch.bank_expenses[i]?.description || "",
        formatAmount(rightBranch.bank_expenses[i]?.amount),
      ]);
    }

    rows.push([
      { content: "Total Bank Payment", styles: headerStyle },
      { content: formatAmount(leftBranch.total_bank_payment), styles: headerStyle },
      { content: "Total Bank Payment", styles: headerStyle },
      { content: formatAmount(rightBranch.total_bank_payment), styles: headerStyle },
    ]);

    doc.setTextColor(0, 0, 0);
    doc.autoTable({
      body: rows,
      startY: 22,
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      margin: { left: 10, right: 10 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 28, halign: "right" },
        2: { cellWidth: 70 },
        3: { cellWidth: 28, halign: "right" },
      },
    });

    doc.save(`Paid_Expenses_${filters.date}.pdf`);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <div className="row">
                <div className="col-md-6">
                  <h5 className="mb-0 font-20">Paid Expense Report</h5>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    className="btn btn-primary mx-2"
                    onClick={generatePdf}
                    disabled={!report}
                  >
                    <i className="bi bi-file-earmark-pdf"></i> PDF
                  </button>
                  <a
                    className="btn btn-success"
                    href={exportLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="bi bi-file-earmark-excel"></i> Excel
                  </a>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={filters.date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={fetchReport}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Get Report"}
                  </button>
                </div>
              </div>

              {!report ? (
                <div className="text-center mt-4">No record found</div>
              ) : (
                <div className="vostro-report-wrapper mt-4">
                  <table className="vostro-report-table w-100">
                    <thead>
                      <tr>
                        <th className="paid-expense-title" colSpan="4">
                          PAID Expenses {moment(filters.date).format("DD-MMM-YYYY")}
                        </th>
                      </tr>
                      <tr>
                        <th className="vostro-report-branch" colSpan="2">F-11 Exp Paid Detail</th>
                        <th className="vostro-report-branch" colSpan="2">G-13 Exp Paid Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="vostro-report-label">Cash from Bank</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.cash_from_bank)}</td>
                        <td className="vostro-report-label">Cash from Bank</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.cash_from_bank)}</td>
                      </tr>
                      <tr>
                        <td className="vostro-report-label">Cash in hand</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.cash_in_hand)}</td>
                        <td className="vostro-report-label">Cash in hand</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.cash_in_hand)}</td>
                      </tr>
                      <tr className="vostro-report-total">
                        <td>Total Cash</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.total_cash)}</td>
                        <td>Total Cash</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.total_cash)}</td>
                      </tr>
                      {Array.from({ length: maxCashRows }).map((_, index) => (
                        <tr key={`cash-${index}`}>
                          <td>{leftBranch.cash_expenses[index]?.description || ""}</td>
                          <td className="vostro-report-value">{formatAmount(leftBranch.cash_expenses[index]?.amount)}</td>
                          <td>{rightBranch.cash_expenses[index]?.description || ""}</td>
                          <td className="vostro-report-value">{formatAmount(rightBranch.cash_expenses[index]?.amount)}</td>
                        </tr>
                      ))}
                      <tr className="vostro-report-total">
                        <td>Total Expense</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.total_expense)}</td>
                        <td>Total Expense</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.total_expense)}</td>
                      </tr>
                      <tr>
                        <td className="vostro-report-label">Balance Cash in Safe</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.balance_cash_in_safe)}</td>
                        <td className="vostro-report-label">Balance Cash in Safe</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.balance_cash_in_safe)}</td>
                      </tr>
                      <tr>
                        <td className="vostro-report-branch" colSpan="2">Bank Payment Detail</td>
                        <td className="vostro-report-branch" colSpan="2">Bank Payment Detail</td>
                      </tr>
                      {Array.from({ length: maxBankRows }).map((_, index) => (
                        <tr key={`bank-${index}`}>
                          <td>{leftBranch.bank_expenses[index]?.description || ""}</td>
                          <td className="vostro-report-value">{formatAmount(leftBranch.bank_expenses[index]?.amount)}</td>
                          <td>{rightBranch.bank_expenses[index]?.description || ""}</td>
                          <td className="vostro-report-value">{formatAmount(rightBranch.bank_expenses[index]?.amount)}</td>
                        </tr>
                      ))}
                      <tr className="vostro-report-total">
                        <td>Total Bank Payment</td>
                        <td className="vostro-report-value">{formatAmount(leftBranch.total_bank_payment)}</td>
                        <td>Total Bank Payment</td>
                        <td className="vostro-report-value">{formatAmount(rightBranch.total_bank_payment)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
